import {
  takeEvery, call, put, select,
} from 'redux-saga/effects'
import UAParser from 'ua-parser-js'

import createTrackingMeta from '@doveconviene/sf-web-js-sdk/createTrackingMeta'
import sanitizeObject from '@doveconviene/sf-web-js-sdk/sanitizeObject'

import { tracking as trackingActions } from 'flux/actions'
import { tracking as trackingApi } from 'flux/api'
import {
  tracking as trackingSelectors,
  property as propertySelectors,
  user as userSelectors,
  router as routerSelectors,
  ui as uiSelectors,
} from 'flux/selectors'

import { getI18nMap } from 'utils/i18n'

const password = process.env.REACT_APP_STREAMFULLY_PASSWORD
const ua = UAParser()

export const base = {
  pn: 'viewer-whitelabel',
  tp: 'w',
  v: '1.0',
}

export const allowedEntries = [
  'et',
  'ct',
  'pn',
  'pid',
  'st',
  'tp',
  'uid',
  'v',
]

export const allowedEventEntries = [
  'event_type',
  'fgid',
  'fid',
  'fp',
  'lat',
  'lng',
  'text',
  'url',
  'utmm',
  'utms',
  'ts',
  'origin',
  'pvv',
  'mode',
  'webnet_sp',
  'webnet_st',
  'sid',
]

export const structureEntries = (entries, meta) => {
  const obj = sanitizeObject(
    {
      ...entries,
      ...meta,
      ...base,
    },
    allowedEntries,
  )
  return {
    et: obj.et,
    h: {
      brn: ua.browser.name,
      brv: ua.browser.version,
      ct: obj.ct,
      dv: ua.device.vendor,
      pid: obj.pid,
      pn: obj.pn,
      sn: ua.os.name,
      sv: ua.os.version,
      tp: obj.tp,
      uid: obj.uid,
    },
    st: obj.st,
    v: obj.v,
  }
}

export const structureEventEntries = (entries) => {
  const obj = sanitizeObject(
    { ...entries },
    allowedEventEntries,
  )
  return {
    at: {
      fgid: obj.fgid,
      fid: obj.fid,
      fp: obj.fp,
      lat: obj.lat,
      lng: obj.lng,
      text: obj.text,
      url: obj.url,
      utmm: obj.utmm,
      utms: obj.utms,
      origin: obj.origin,
      pvv: obj.pvv,
      mode: obj.mode,
      webnet_sp: obj.webnet_sp,
      webnet_st: obj.webnet_st,
      sid: obj.sid,
    },
    et: obj.event_type,
    ts: obj.ts || Date.now(),
  }
}

export function* trackStreamfullySetup(action) {
  const { entries, customEntries, customEventEntries } = action.payload
  const uid = yield select(userSelectors.selectUserId)
  const {
    id: pid,
    extras: { web_network: webNetworkInfo = { active: '0' } } = {},
  } = yield select(propertySelectors.selectProperty) || {}
  const language = yield select(uiSelectors.selectLanguage),
    i18n = getI18nMap(language),
    country = i18n.ISO3166_1

  const meta = createTrackingMeta(password)
  const isWebNetworkActive = webNetworkInfo && webNetworkInfo.active === '1'
  const structuredEntries = structureEntries({
    uid,
    ct: country,
    ...customEntries,
    ...(!isWebNetworkActive && { pid }),
  }, meta)
  const { lat, lng, label } = yield select(trackingSelectors.selectTrackingGeolocation)

  const structuredEventEntries = entries.map(event => structureEventEntries({
    ...event,
    ...customEventEntries,
    lat,
    lng,
    label,
  }))
  const customTrackingUrl = isWebNetworkActive && webNetworkInfo.streamfully_tracking_url
  try {
    yield call(trackingApi.trackStreamfully, {
      ...structuredEntries,
      e: structuredEventEntries,
    },
    customTrackingUrl)
  } catch (e) {
    yield put(trackingActions.trackingError(e, action))
  }
}

export function* trackStreamfully(action) {
  const { entries } = action.payload

  const {
    extras: { web_network: webNetworkInfo = { active: '0' } } = {},
  } = yield select(propertySelectors.selectProperty) || {}
  const isWebNetworkActive = webNetworkInfo && webNetworkInfo.active === '1'
  const {
    utm_medium: utmm,
    utm_source: utmSourceDefault,
    ...query
  } = yield select(routerSelectors.selectRouterQuery)
  const utms = (isWebNetworkActive && webNetworkInfo.utm_source) || utmSourceDefault
  const customEventEntries = { utms, utmm, ...query }
  const payload = { entries, customEventEntries }
  yield put(trackingActions.trackStreamfullySetup(payload))
}

export function* trackStreamfullyInit(action) {
  const { entries } = action.payload

  const sid = yield select(userSelectors.selectUserSessionId)
  const {
    id: pid,
    extras: { web_network: webNetworkInfo = { active: '0' } } = {},
  } = yield select(propertySelectors.selectProperty) || {}
  const isWebNetworkActive = webNetworkInfo && webNetworkInfo.active === '1'

  if (isWebNetworkActive && webNetworkInfo.utm_source && webNetworkInfo.session_tracking) {
    const utms = webNetworkInfo.utm_source
    const customEntries = { pid }
    const customEventEntries = { sid, utms }
    const payload = { entries, customEntries, customEventEntries }
    yield put(trackingActions.trackStreamfullySetup(payload))
  }
}


export default function* () {
  yield takeEvery(trackingActions.types.STREAMFULLY, trackStreamfully)
  yield takeEvery(trackingActions.types.STREAMFULLY_INIT, trackStreamfullyInit)
  yield takeEvery(trackingActions.types.STREAMFULLY_SETUP, trackStreamfullySetup)
}
